import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Crown, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WHIP-styled leadership roster for the Profiles page header.
// Shown above the member grid, supports both-chamber or single-chamber view
// driven by the page's existing `filterChamber` state so nothing else changes.
//
// Props:
//   chamberFilter: "all" | "House" | "Senate"

const PARTY_BG = {
  Republican: "border-red-500/40 bg-red-500/[0.06]",
  Democratic: "border-blue-500/40 bg-blue-500/[0.06]",
  Independent: "border-white/20 bg-white/[0.04]",
};
const PARTY_CHIP = {
  Republican: "bg-red-500/20 text-red-200 border-red-500/40",
  Democratic: "bg-blue-500/20 text-blue-200 border-blue-500/40",
  Independent: "bg-white/10 text-white/70 border-white/20",
};

function LeadershipCard({ member }) {
  const seatLabel =
    member.district && member.district !== "None"
      ? `${member.state}-${member.district}`
      : member.state || "";
  return (
    <Link
      to={`/legislator/${member.legislatorId}`}
      className={
        "group relative flex items-center gap-3 rounded-xl border px-3 py-3 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-orange-500/10 hover:border-orange-500/50 " +
        (PARTY_BG[member.party] || PARTY_BG.Independent)
      }
      data-testid={`leadership-card-${member.bioguideID}`}
    >
      {/* Avatar */}
      <div className="shrink-0 w-12 h-12 rounded-full overflow-hidden bg-white/5 border border-white/15 ring-1 ring-white/10">
        {member.imageUrl ? (
          <img
            src={member.imageUrl}
            alt={member.name}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/35 text-xs">
            ?
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-orange-300/80 font-semibold">
            {member.position}
          </span>
        </div>
        <p className="text-sm font-bold text-white truncate group-hover:text-orange-200 transition-colors">
          {member.name}
        </p>
        <p className="text-xs text-white/55 truncate">
          <span
            className={
              "inline-block px-1.5 rounded text-[10px] mr-1 border " +
              (PARTY_CHIP[member.party] || PARTY_CHIP.Independent)
            }
          >
            {member.party?.[0] || "?"}
          </span>
          {seatLabel}
        </p>
      </div>
    </Link>
  );
}

function ChamberBlock({ chamber, members, icon: Icon }) {
  if (!members || members.length === 0) return null;
  const majority = members.filter((m) => m.caucus === "majority");
  const minority = members.filter((m) => m.caucus === "minority");
  return (
    <Card
      className="p-5 rounded-2xl border border-white/10 whip-glow"
      style={{ background: "linear-gradient(160deg, rgba(255,138,0,0.05), rgba(11,11,15,0.85))" }}
      data-testid={`leadership-block-${chamber.toLowerCase()}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-orange-400" />
        <h2 className="text-sm uppercase tracking-[0.18em] font-bold text-white">
          {chamber} Leadership
        </h2>
        <Badge variant="outline" className="ml-auto bg-white/5 border-white/15 text-white/70 text-xs">
          119th Congress
        </Badge>
      </div>

      {majority.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wider text-red-300/80 mb-2 font-semibold">
            Majority. Republican
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-2">
            {majority.map((m) => (
              <LeadershipCard key={m.bioguideID} member={m} />
            ))}
          </div>
        </div>
      )}

      {minority.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-blue-300/80 mb-2 font-semibold">
            Minority. Democratic
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-2">
            {minority.map((m) => (
              <LeadershipCard key={m.bioguideID} member={m} />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function LeadershipPanel({ chamberFilter = "all" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/leadership`)
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && setErr(e.message || "failed"));
    return () => {
      cancelled = true;
    };
  }, []);

  if (err || !data) return null; // fail-silent, leadership is a bonus panel

  const showHouse = chamberFilter === "all" || chamberFilter === "House";
  const showSenate = chamberFilter === "all" || chamberFilter === "Senate";

  return (
    <div className="mb-8" data-testid="leadership-panel">
      <div className={"grid gap-4 " + (showHouse && showSenate ? "lg:grid-cols-2" : "")}>
        {showHouse && <ChamberBlock chamber="House" members={data.house} icon={Crown} />}
        {showSenate && <ChamberBlock chamber="Senate" members={data.senate} icon={Shield} />}
      </div>
    </div>
  );
}
